import { useViewport } from '@travelata-libs/utils';
import Vue from 'vue';

export function validateRequiredCheckboxes(viewModel, requiredKeys, container = document) {
	let valid = true;

	requiredKeys.forEach((key) => {
		if (key === 'agreeAdvertising') {
			const isLocked = viewModel.get('advertisingConsentLocked');
			const isChecked = viewModel.get('agreeAdvertising');

			if (isLocked && isChecked) {
				return;
			}
		}

		const label = container.querySelector(`[data-checkbox="${key}"]`);
		const icon = label?.querySelector('.checkbox-item-common__icon');

		if (viewModel.get(key)) {
			icon?.classList.remove('checkbox-error');
		} else {
			icon?.classList.add('checkbox-error');
			valid = false;
		}
	});

	return valid;
}

export function getCheckboxConfigs(viewModel, idPrefix = '', themeIcon) {
	const { isMobile } = useViewport();
	const advertisingConsentLocked = viewModel.get('advertisingConsentLocked');
	const agreeAdvertising = viewModel.get('agreeAdvertising');

	const advertisingConfig = {
		id: `${idPrefix}agreeAdvertising`,
		modelKey: 'agreeAdvertising',
		labelVNode: advertisingConsentLocked
			? (h) => h('span', [
				h('a', {
					attrs: { href: '/consent-to-advertising', target: '_blank', class: 'link' }
				}, 'Согласие на получение рекламных сообщений'),
				h('span', {
					class: {
						'checkbox-item-common__text-locked-secondary': true,
						'checkbox-item-common-mobile__text-locked-secondary': isMobile.value,
					}
				}, ' предоставлено ранее и может быть отозвано в любой момент'),
			])
			: (h) => h('span', [
				'Я даю ',
				h('a', {
					attrs: { href: '/consent-to-advertising', target: '_blank', class: 'link' }
				}, 'согласие на получение рекламных сообщений')
			]),
		disabled: advertisingConsentLocked,
		locked: advertisingConsentLocked,
		iconLocked: advertisingConsentLocked,
		iconHidden: false,
		isChecked: agreeAdvertising || advertisingConsentLocked,
		themeIcon,
	};

	return [
		{
			id: `${idPrefix}agreeUserAgreement`,
			modelKey: 'agreeUserAgreement',
			labelVNode: (h) => h('span', [
				'Я принимаю ',
				h('a', { attrs: { href: '/user-agreement.html', target: '_blank', class: 'link' } }, 'Пользовательское соглашение'),
				' и ',
				h('a', { attrs: { href: '/privacy.html', target: '_blank', class: 'link' } }, 'Политику о персональных данных')
			]),
			disabled: false,
			isChecked: viewModel.get('agreeUserAgreement'),
		},
		{
			id: `${idPrefix}agreePersonalData`,
			modelKey: 'agreePersonalData',
			labelVNode: (h) => h('span', [
				'Я даю ',
				h('a', { attrs: { href: '/personal-data-agreement.html', target: '_blank', class: 'link' } }, 'согласие на обработку персональных данных')
			]),
			disabled: false,
			isChecked: viewModel.get('agreePersonalData'),
		},
		advertisingConfig,
		{
			id: `${idPrefix}agreeDisseminationAndPersonalData`,
			modelKey: 'agreeDisseminationAndPersonalData',
			labelVNode: (h) => h('span', [
				'Даю ',
				h('a', { attrs: { href: '/personal-data-agreement.html', target: '_blank', class: 'link' } }, 'согласие на обработку'),
				' и ',
				h('a', { attrs: { href: '/personal-data-transfer-consent.html', target: '_blank', class: 'link' } }, 'распространение персональных данных ')
			]),
			disabled: false,
			isChecked: viewModel.get('agreeDisseminationAndPersonalData'),
		}
	];
}
export function mountCheckboxes(checkboxConfigs, viewModel, CheckBoxInputCommon, container = document) {
	checkboxConfigs.forEach(checkboxConfig => {
		const el = container.querySelector(`#${checkboxConfig.id}`);
		if (!el) return;

		if (el.__vueInstance) {
			el.__vueInstance.$destroy();
			el.__vueInstance = null;
		}

		el.innerHTML = '';

		el.__vueInstance = new Vue({
			render: h => h(CheckBoxInputCommon, {
				props: {
					isChecked: viewModel.get(checkboxConfig.modelKey),
					value: true,
					disabled: checkboxConfig.disabled,
					isAuthOrRegistration: true,
					checkboxKey: checkboxConfig.modelKey,
					domId: checkboxConfig.id,
					iconLocked: checkboxConfig.iconLocked,
					labelClass: checkboxConfig.labelClass ?? '',
					iconHidden: checkboxConfig.iconHidden,
				},
				on: {
					selected: ({ checked }) => {
						viewModel.set(checkboxConfig.modelKey, checked);
					}
				}
			}, checkboxConfig.labelVNode ? [checkboxConfig.labelVNode(h)] : undefined),
		}).$mount(el);
	});
}

let lastCheckedEmail = '';
let debounceTimeout = null;

async function checkEmailConsent(email, apiUrl) {
	const emailUrl = `${apiUrl}/frontend/subscriptions/contacts/getByEmail`;
	const emailResponse = await $.ajax({
		url: emailUrl,
		type: 'GET',
		data: { email },
	});

	return emailResponse.success && emailResponse.result
		? { hasConsent: !!emailResponse.result.agreedWithPolicy, result: emailResponse.result }
		: { hasConsent: false, result: null };
}

async function checkSmsConsent(email, apiUrl) {
	try {
		const consentUrl = `${apiUrl}/frontend/customer/consents`;
		const consentResponse = await $.ajax({
			url: consentUrl,
			type: 'GET',
			data: { email, scope: 'marketing' },
		});

		return consentResponse.success && consentResponse.result
			? !!consentResponse.result.agreed
			: false;
	} catch (error) {
		console.warn('SMS consent check failed:', error);
		return false;
	}
}

export function fetchAndSyncAdvertisingConsent({
																								 email,
																								 consentModel,
																								 getCheckboxConfigs,
																								 mountCheckboxes,
																								 CheckBoxInputCommon,
																								 onComplete,
																								 checkSms = false,
																							 }) {
	if (!email || email === lastCheckedEmail) return;

	clearTimeout(debounceTimeout);
	debounceTimeout = setTimeout(async () => {
		lastCheckedEmail = email;

		const apiUrl = window.appConfig.apiGatewayUrl;
		let finalResult = null;

		try {
			const emailCheck = await checkEmailConsent(email, apiUrl);
			const hasEmailConsent = emailCheck.hasConsent;
			finalResult = emailCheck.result;

			let shouldLockAdvertising = false;

			if (checkSms) {
				const hasSmsConsent = await checkSmsConsent(email, apiUrl);
				const bothConsentsGiven = hasEmailConsent && hasSmsConsent;
				shouldLockAdvertising = bothConsentsGiven;
			} else {
				shouldLockAdvertising = hasEmailConsent;
			}

			consentModel.set('advertisingConsentLocked', shouldLockAdvertising);
			consentModel.set('agreeAdvertising', shouldLockAdvertising);

		} catch (error) {
			console.warn('Error fetching advertising consent:', error);
			consentModel.set('advertisingConsentLocked', false);
			consentModel.set('agreeAdvertising', false);
		} finally {
			const checkboxConfigs = getCheckboxConfigs(consentModel, 'checkbox-');
			mountCheckboxes(checkboxConfigs, consentModel, CheckBoxInputCommon);
			onComplete?.(finalResult);
		}
	}, 300);
}